import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Plus, Trash2, PlayCircle, Mail, MessageSquare, Tag, TagsIcon, MoveRight, Clock,
  Bell, Webhook, Save, Loader2, GitBranch, Target, Flag, ChevronDown, ChevronRight, Send,
} from "lucide-react";
import { toast } from "sonner";

const TRIGGERS = [
  { value: "contact_created", label: "Contact created" },
  { value: "tag_added", label: "Tag added" },
  { value: "stage_changed", label: "Pipeline stage changed" },
  { value: "form_submitted", label: "Form submitted" },
  { value: "appointment_booked", label: "Appointment booked" },
  { value: "appointment_rescheduled", label: "Appointment rescheduled" },
  { value: "appointment_cancelled", label: "Appointment cancelled" },
  { value: "appointment_noshow", label: "Appointment no-show" },
];
const ACTIONS = [
  { type: "send_email", label: "Send email", icon: Mail, color: "#6366F1" },
  { type: "send_sms", label: "Send SMS", icon: MessageSquare, color: "#06B6D4" },
  { type: "send_telegram", label: "Send Telegram", icon: Send, color: "#229ED9" },
  { type: "add_tag", label: "Add tag", icon: Tag, color: "#10B981" },
  { type: "remove_tag", label: "Remove tag", icon: TagsIcon, color: "#F59E0B" },
  { type: "move_stage", label: "Move pipeline stage", icon: MoveRight, color: "#8B5CF6" },
  { type: "wait", label: "Wait / delay", icon: Clock, color: "#A855F7" },
  { type: "notify", label: "Internal notification", icon: Bell, color: "#F43F5E" },
  { type: "webhook", label: "Webhook", icon: Webhook, color: "#94A3B8" },
  { type: "condition", label: "If / Else branch", icon: GitBranch, color: "#EAB308" },
];
const meta = (t) => ACTIONS.find((a) => a.type === t) || { label: t, icon: Flag, color: "#94A3B8" };
const genId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const STATUS_COLORS = { active: "#06B6D4", completed: "#10B981", goal_met: "#8B5CF6", errored: "#F43F5E", removed: "#94A3B8" };

// ---- immutable tree helpers ----
function updateStep(steps, id, updater) {
  return steps.map((s) => {
    if (s.id === id) return updater(s);
    if (s.type === "condition") {
      const cfg = s.config || {};
      return { ...s, config: { ...cfg, yes_steps: updateStep(cfg.yes_steps || [], id, updater), no_steps: updateStep(cfg.no_steps || [], id, updater) } };
    }
    return s;
  });
}
function removeStep(steps, id) {
  return steps.filter((s) => s.id !== id).map((s) => {
    if (s.type === "condition") {
      const cfg = s.config || {};
      return { ...s, config: { ...cfg, yes_steps: removeStep(cfg.yes_steps || [], id), no_steps: removeStep(cfg.no_steps || [], id) } };
    }
    return s;
  });
}
function addToBranch(steps, parentId, branch, step) {
  return steps.map((s) => {
    if (s.id === parentId) {
      const cfg = s.config || {};
      const key = branch === "yes" ? "yes_steps" : "no_steps";
      return { ...s, config: { ...cfg, [key]: [...(cfg[key] || []), step] } };
    }
    if (s.type === "condition") {
      const cfg = s.config || {};
      return { ...s, config: { ...cfg, yes_steps: addToBranch(cfg.yes_steps || [], parentId, branch, step), no_steps: addToBranch(cfg.no_steps || [], parentId, branch, step) } };
    }
    return s;
  });
}
function newStep(type) {
  if (type === "condition") return { id: genId(), type, config: { match: "all", conditions: [{ field: "tag", op: "has_tag", value: "" }], yes_steps: [], no_steps: [] } };
  if (type === "wait") return { id: genId(), type, config: { mode: "duration", days: 0, hours: 0, minutes: 5 } };
  return { id: genId(), type, config: {} };
}

export default function AutomationBuilder() {
  const { workflowId } = useParams();
  const { activeSubId } = useAuth();
  const navigate = useNavigate();
  const [wf, setWf] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const [{ data }, { data: p }, { data: m }] = await Promise.all([
      api.get(`/automations/${workflowId}`),
      api.get(`/pipelines?sub_account_id=${activeSubId}`),
      api.get(`/automations/meta`),
    ]);
    data.goal = data.goal && Object.keys(data.goal).length ? data.goal : { enabled: false, match: "any", conditions: [] };
    setWf(data); setPipelines(p); setFields(m.condition_fields); setOperators(m.condition_operators);
  }, [workflowId, activeSubId]);
  useEffect(() => { load(); }, [load]);

  if (!wf) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const setSteps = (steps) => setWf({ ...wf, steps });
  const addStep = (type, target) => {
    const step = newStep(type);
    if (!target) setSteps([...wf.steps, step]);
    else setSteps(addToBranch(wf.steps, target.parentId, target.branch, step));
  };
  const updateCfg = (id, patch) => setSteps(updateStep(wf.steps, id, (s) => ({ ...s, config: { ...s.config, ...patch } })));
  const delStep = (id) => setSteps(removeStep(wf.steps, id));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/automations/${workflowId}`, {
        name: wf.name, trigger_type: wf.trigger_type, trigger_config: wf.trigger_config,
        steps: wf.steps, enabled: wf.enabled, enrollment_rule: wf.enrollment_rule, goal: wf.goal,
      });
      toast.success("Automation saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const a = wf.analytics || {};

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl mx-auto pb-16">
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => navigate("/automations")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-automations">
          <ArrowLeft className="w-4 h-4" /> Automations
        </button>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={wf.enabled} onCheckedChange={(v) => setWf({ ...wf, enabled: v })} data-testid="builder-enabled-switch" />
            <span className="text-sm text-muted-foreground">{wf.enabled ? "Live" : "Paused"}</span>
          </div>
          <Button onClick={save} disabled={saving} className="gap-2 font-semibold" data-testid="save-automation-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
        </div>
      </div>

      <Input value={wf.name} onChange={(e) => setWf({ ...wf, name: e.target.value })}
        className="text-2xl font-display font-bold h-auto py-2 border-0 bg-transparent px-0 focus-visible:ring-0" data-testid="builder-name-input" />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { k: "entered", label: "Entered", c: "#6366F1" },
          { k: "active", label: "Active", c: "#06B6D4" },
          { k: "completed", label: "Completed", c: "#10B981" },
          { k: "goal_met", label: "Goal met", c: "#8B5CF6" },
          { k: "conversion_rate", label: "Conv. rate", c: "#F59E0B", suffix: "%" },
        ].map((m) => (
          <Card key={m.k} className="p-3.5 bg-card border-border/70" data-testid={`analytics-${m.k}`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{m.label}</p>
            <p className="text-2xl font-display font-bold mt-1" style={{ color: m.c }}>{a[m.k] ?? 0}{m.suffix || ""}</p>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="build">
        <TabsList>
          <TabsTrigger value="build" data-testid="tab-build">Builder</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Contact logs ({wf.enrollments?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="build" className="mt-6 space-y-4">
          {/* Enrollment + Goal settings */}
          <Card className="p-5 bg-card border-border/70 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-display font-semibold text-sm">Enrollment</p>
                <p className="text-xs text-muted-foreground">Should a contact be able to re-enter this workflow?</p>
              </div>
              <Select value={wf.enrollment_rule} onValueChange={(v) => setWf({ ...wf, enrollment_rule: v })}>
                <SelectTrigger className="w-[180px]" data-testid="enrollment-rule-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One time only</SelectItem>
                  <SelectItem value="re_entry">Allow re-entry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-border/60 pt-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-violet-400" />
                  <div>
                    <p className="font-display font-semibold text-sm">Goal</p>
                    <p className="text-xs text-muted-foreground">Pull contacts out early when they meet this condition</p>
                  </div>
                </div>
                <Switch checked={!!wf.goal.enabled} onCheckedChange={(v) => setWf({ ...wf, goal: { ...wf.goal, enabled: v, conditions: wf.goal.conditions?.length ? wf.goal.conditions : [{ field: "tag", op: "has_tag", value: "" }] } })} data-testid="goal-enabled-switch" />
              </div>
              {wf.goal.enabled && (
                <div className="mt-3" data-testid="goal-editor">
                  <ConditionGroup group={wf.goal} fields={fields} operators={operators}
                    onChange={(g) => setWf({ ...wf, goal: { ...wf.goal, ...g } })} testPrefix="goal" />
                </div>
              )}
            </div>
          </Card>

          {/* Trigger */}
          <Card className="p-6 bg-card border-border/70">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle className="w-5 h-5 text-primary" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">When this happens</span>
            </div>
            <Select value={wf.trigger_type} onValueChange={(v) => setWf({ ...wf, trigger_type: v, trigger_config: {} })}>
              <SelectTrigger data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            {wf.trigger_type === "tag_added" && (
              <div className="mt-3 space-y-1.5">
                <Label>Only when this tag is added</Label>
                <Input value={wf.trigger_config?.tag || ""} onChange={(e) => setWf({ ...wf, trigger_config: { tag: e.target.value } })}
                  placeholder="e.g. hot-lead (blank = any tag)" data-testid="trigger-tag-input" />
              </div>
            )}
          </Card>

          <Connector />
          <StepList steps={wf.steps} target={null} pipelines={pipelines} fields={fields} operators={operators}
            addStep={addStep} updateCfg={updateCfg} delStep={delStep} depth={0} />
        </TabsContent>

        <TabsContent value="log" className="mt-6 space-y-3">
          {(wf.enrollments || []).length === 0 && <p className="text-sm text-muted-foreground">No contacts have entered this workflow yet.</p>}
          {(wf.enrollments || []).map((e) => {
            const open = expanded[e.id];
            return (
              <Card key={e.id} className="bg-card border-border/70 overflow-hidden" data-testid={`enrollment-${e.contact_id}`}>
                <button onClick={() => setExpanded({ ...expanded, [e.id]: !open })} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/40 transition-colors">
                  {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span className="font-medium text-sm flex-1 text-left">{e.contact_name}</span>
                  <Badge variant="secondary" className="capitalize" style={{ color: STATUS_COLORS[e.status] }}>{e.status.replace("_", " ")}</Badge>
                  <span className="text-xs text-muted-foreground font-mono hidden sm:inline">{new Date(e.updated_at).toLocaleString()}</span>
                </button>
                {open && (
                  <div className="px-5 pb-4 pt-1 space-y-2 border-t border-border/60">
                    {e.history.map((h, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <Badge variant="secondary" className="text-[10px] mt-0.5"
                          style={{ color: h.status === "error" ? "#F43F5E" : h.status === "skipped" ? "#94A3B8" : h.status === "success" ? "#10B981" : "#6B7280" }}>
                          {h.status}
                        </Badge>
                        <div className="flex-1">
                          <span className="font-mono text-xs text-muted-foreground">{h.step_type}</span>
                          <p className="text-muted-foreground">{h.detail}</p>
                        </div>
                        <span className="text-[10px] text-muted-foreground/60 font-mono">{new Date(h.at).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Connector() {
  return <div className="flex justify-center"><div className="w-px h-6 bg-border" /></div>;
}

function StepList({ steps, target, pipelines, fields, operators, addStep, updateCfg, delStep, depth }) {
  return (
    <div className="space-y-4">
      {steps.map((step) => (
        <React.Fragment key={step.id}>
          <StepCard step={step} pipelines={pipelines} fields={fields} operators={operators}
            addStep={addStep} updateCfg={updateCfg} delStep={delStep} depth={depth} />
          <Connector />
        </React.Fragment>
      ))}
      <AddStepButton target={target} addStep={addStep} depth={depth} />
    </div>
  );
}

function AddStepButton({ target, addStep, depth }) {
  const tid = target ? `add-step-${target.parentId}-${target.branch}` : "add-step-button";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={`w-full gap-2 border-dashed ${depth ? "h-11" : "h-14"}`} data-testid={tid}>
          <Plus className="w-4 h-4" /> Add {depth ? "step" : "action"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        {ACTIONS.map((act) => (
          <DropdownMenuItem key={act.type} onClick={() => addStep(act.type, target)} className="gap-2 cursor-pointer" data-testid={`add-action-${act.type}${target ? "-" + target.branch : ""}`}>
            <act.icon className="w-4 h-4" style={{ color: act.color }} /> {act.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StepCard({ step, pipelines, fields, operators, addStep, updateCfg, delStep, depth }) {
  const m = meta(step.type);
  const isCond = step.type === "condition";
  return (
    <Card className={`p-5 bg-card border-border/70 ${isCond ? "border-yellow-500/30" : ""}`} data-testid={`step-${step.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${m.color}22` }}>
          <m.icon className="w-4 h-4" style={{ color: m.color }} />
        </div>
        <span className="font-display font-semibold flex-1">{m.label}</span>
        <button onClick={() => delStep(step.id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-step-${step.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {isCond ? (
        <div className="space-y-4">
          <ConditionGroup group={step.config} fields={fields} operators={operators}
            onChange={(g) => updateCfg(step.id, g)} testPrefix={`cond-${step.id}`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["yes", "no"].map((branch) => (
              <div key={branch} className="rounded-xl border border-border/60 p-3" data-testid={`branch-${branch}-${step.id}`}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: branch === "yes" ? "#10B981" : "#F43F5E" }}>
                  {branch === "yes" ? "If TRUE" : "If FALSE"}
                </p>
                <StepList steps={step.config?.[`${branch}_steps`] || []} target={{ parentId: step.id, branch }}
                  pipelines={pipelines} fields={fields} operators={operators}
                  addStep={addStep} updateCfg={updateCfg} delStep={delStep} depth={depth + 1} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <StepConfig step={step} update={(c) => updateCfg(step.id, c)} pipelines={pipelines} />
      )}
    </Card>
  );
}

function ConditionGroup({ group, fields, operators, onChange, testPrefix }) {
  const conds = group.conditions || [];
  const setConds = (c) => onChange({ ...group, conditions: c });
  const update = (i, patch) => setConds(conds.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const noVal = (op) => ["is_empty", "is_not_empty"].includes(op);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Match</span>
        <Select value={group.match || "all"} onValueChange={(v) => onChange({ ...group, match: v })}>
          <SelectTrigger className="w-24 h-8" data-testid={`${testPrefix}-match`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">ALL</SelectItem><SelectItem value="any">ANY</SelectItem></SelectContent>
        </Select>
        <span className="text-muted-foreground">of the following</span>
      </div>
      {conds.map((c, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-row-${i}`}>
          <Select value={c.field} onValueChange={(v) => update(i, { field: v })}>
            <SelectTrigger className="w-[130px] h-9" data-testid={`${testPrefix}-field-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{fields.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
          {c.field === "custom" && (
            <Input value={c.custom_key || ""} onChange={(e) => update(i, { custom_key: e.target.value })} placeholder="field key" className="w-[110px] h-9" data-testid={`${testPrefix}-key-${i}`} />
          )}
          <Select value={c.op} onValueChange={(v) => update(i, { op: v })}>
            <SelectTrigger className="w-[150px] h-9" data-testid={`${testPrefix}-op-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{operators.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          {!noVal(c.op) && (
            <Input value={c.value || ""} onChange={(e) => update(i, { value: e.target.value })} placeholder="value" className="w-[120px] h-9" data-testid={`${testPrefix}-value-${i}`} />
          )}
          <button onClick={() => setConds(conds.filter((_, k) => k !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`${testPrefix}-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => setConds([...conds, { field: "tag", op: "has_tag", value: "" }])} data-testid={`${testPrefix}-add-condition`}>
        <Plus className="w-3.5 h-3.5" /> Add condition
      </Button>
    </div>
  );
}

function StepConfig({ step, update, pipelines }) {
  const c = step.config || {};
  if (step.type === "send_email")
    return (
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Subject</Label><Input value={c.subject || ""} onChange={(e) => update({ subject: e.target.value })} data-testid="email-subject-input" /></div>
        <div className="space-y-1.5"><Label>Body</Label>
          <Textarea value={c.body || ""} onChange={(e) => update({ body: e.target.value })} className="min-h-[120px]" placeholder="Hi {{first_name}}, ..." data-testid="email-body-input" />
          <p className="text-xs text-muted-foreground">Use {"{{first_name}}"} or {"{{name}}"} to personalize.</p></div>
      </div>
    );
  if (step.type === "send_sms")
    return (
      <div className="space-y-1.5"><Label>SMS message</Label>
        <Textarea value={c.body || ""} onChange={(e) => update({ body: e.target.value })} className="min-h-[80px]" data-testid="sms-body-input" />
        <p className="text-xs text-muted-foreground">Twilio not connected yet — messages log to the contact thread.</p></div>
    );
  if (step.type === "send_telegram")
    return (
      <div className="space-y-1.5"><Label>Telegram message</Label>
        <Textarea value={c.body || ""} onChange={(e) => update({ body: e.target.value })} className="min-h-[80px]" data-testid="telegram-body-input" />
        <p className="text-xs text-muted-foreground">Sent free to contacts who've connected your Telegram bot.</p></div>
    );
  if (step.type === "add_tag" || step.type === "remove_tag")
    return <div className="space-y-1.5"><Label>Tag</Label><Input value={c.tag || ""} onChange={(e) => update({ tag: e.target.value })} placeholder="e.g. welcomed" data-testid="tag-config-input" /></div>;
  if (step.type === "move_stage") {
    const pipe = pipelines.find((p) => p.id === c.pipeline_id);
    return (
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Pipeline</Label>
          <Select value={c.pipeline_id || ""} onValueChange={(v) => update({ pipeline_id: v, stage_id: "" })}>
            <SelectTrigger data-testid="move-pipeline-select"><SelectValue placeholder="Select pipeline" /></SelectTrigger>
            <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select></div>
        {pipe && (
          <div className="space-y-1.5"><Label>Stage</Label>
            <Select value={c.stage_id || ""} onValueChange={(v) => update({ stage_id: v })}>
              <SelectTrigger data-testid="move-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>{pipe.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
      </div>
    );
  }
  if (step.type === "wait")
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Mode</span>
          <Select value={c.mode || "duration"} onValueChange={(v) => update({ mode: v })}>
            <SelectTrigger className="w-[180px] h-9" data-testid="wait-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="duration">Wait a duration</SelectItem><SelectItem value="until">Wait until day/time</SelectItem></SelectContent>
          </Select>
        </div>
        {(c.mode || "duration") === "duration" ? (
          <div className="grid grid-cols-3 gap-3">
            {[["days", "Days"], ["hours", "Hours"], ["minutes", "Minutes"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label>
                <Input type="number" min="0" value={c[k] ?? 0} onChange={(e) => update({ [k]: parseInt(e.target.value) || 0 })} data-testid={`wait-${k}-input`} /></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Day of week</Label>
              <Select value={String(c.until_weekday ?? "")} onValueChange={(v) => update({ until_weekday: v === "any" ? "" : parseInt(v) })}>
                <SelectTrigger data-testid="wait-weekday-select"><SelectValue placeholder="Any day" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any day</SelectItem>
                  {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Time (UTC)</Label>
              <Input type="time" value={c.until_time || "09:00"} onChange={(e) => update({ until_time: e.target.value })} data-testid="wait-time-input" /></div>
          </div>
        )}
      </div>
    );
  if (step.type === "notify")
    return <div className="space-y-1.5"><Label>Notification message</Label><Input value={c.message || ""} onChange={(e) => update({ message: e.target.value })} data-testid="notify-message-input" /></div>;
  if (step.type === "webhook")
    return <div className="space-y-1.5"><Label>Webhook URL</Label><Input value={c.url || ""} onChange={(e) => update({ url: e.target.value })} placeholder="https://…" className="font-mono text-sm" data-testid="webhook-url-input" /></div>;
  return null;
}
